import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'
import { setJsonSettings } from '../../store/session/actions'
import { saveJsonSettings } from '../../store/session/action-creators'

interface ColumnWidthSettings {
  order?: string[]
  widths?: Record<string, number>
}

const selectSession = (state: any) => state.session

const getColumnSettings = (state: any, pathname: string): ColumnWidthSettings => {
  const jsonSettings = state?.session?.sessionData?.jsonSettings?.[pathname]

  if (!jsonSettings) {
    return { order: [], widths: {} }
  }

  if (Array.isArray(jsonSettings)) {
    return { order: jsonSettings, widths: {} }
  }

  return {
    order: jsonSettings.order || [],
    widths: jsonSettings.widths || {}
  }
}

export const useColumnSettings = () => {
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()
  const session = useSelector(selectSession)

  const settings = useMemo(
    () => getColumnSettings({ session }, pathname),
    [session, pathname]
  )

  const updateColumnWidth = useCallback(
    (columnKey: string, width: number) => {
      const newSettings: ColumnWidthSettings = {
        order: settings.order,
        widths: {
          ...settings.widths,
          [columnKey]: width,
        },
      }

      dispatch(
        setJsonSettings({
          [pathname]: newSettings,
        })
      )

      dispatch(saveJsonSettings())
    },
    [settings, dispatch, pathname]
  )

  const updateColumnOrder = useCallback(
    (order: string[]) => {
      const newSettings: ColumnWidthSettings = {
        order,
        widths: settings.widths,
      }

      dispatch(
        setJsonSettings({
          [pathname]: newSettings,
        })
      )

      dispatch(saveJsonSettings())
    },
    [settings.widths, dispatch, pathname]
  )

  return {
    columnOrder: settings.order,
    columnWidths: settings.widths,
    updateColumnWidth,
    updateColumnOrder,
  }
}
